import React, { Fragment, useState, useEffect, useContext, useRef } from 'react';
import { withRouter } from 'react-router-dom';
import Select, { components } from 'react-select';
import { Listbox, Menu, Transition } from '@headlessui/react';
import { ChevronDownIcon, CheckIcon } from '@heroicons/react/solid';
import axios from 'axios';
import ls from 'local-storage'; // Ensure you have this or use an alternative
import Context from 'views/Context/Context';
import { Container } from 'reactstrap';
import Connexion from 'Connexion';
import PopupHost from 'components/Popups/PopupHost';
import { message as antdMessage } from 'antd';
import { BASE_URL, SECRET_KEY_ENCRYPT, Disconnected } from './../../utils/config';
import Joyride from 'react-joyride';
import { useQuery, QueryClient, QueryClientProvider } from '@tanstack/react-query';

// Import your SVG icons as React Components
import { ReactComponent as Person } from './icons/person.svg';
import { ReactComponent as Material } from './icons/material.svg';
import { ReactComponent as Integration } from './icons/integration.svg';
import { ReactComponent as Cm_en } from './icons/Cm_en.svg';
import { ReactComponent as Docs } from './icons/documentation.svg';
import { ReactComponent as Facturation } from './icons/facturation.svg';
import { ReactComponent as Reglage } from './icons/reglage.svg';
import { ReactComponent as Remise } from './icons/remise.svg';
import { ReactComponent as Stock } from './icons/stock.svg';
import { ReactComponent as Client } from './icons/client.svg';
import { ReactComponent as Catalogue } from './icons/catalogue.svg';
import { ReactComponent as FooterSvg } from './icons/footer.svg';
import { ReactComponent as Logo } from './icons/logo.svg';
import { ReactComponent as Tableau } from './icons/tableau.svg';
import { ReactComponent as Cm_enLigne } from './icons/cm_enligne.svg';
import aiAssistantGif from './icons/ai.gif';
import './menuOptions.css';
import { t, tm } from '../../i18n';
import LanguageSwitcher from '../../i18n/LanguageSwitcher';
 


// Import md5 and cryptr libraries
import md5 from 'md5'; // Install via npm: npm install md5
import Cryptr from 'cryptr'; // Install via npm: npm install cryptr

// Initialize Cryptr with your secret key
const cryptr = new Cryptr(SECRET_KEY_ENCRYPT);

// Custom styles for react-select (brand colours, same height for both fields)
const customSelectStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: 54,
    backgroundColor: 'white',
    borderColor: state.isFocused ? '#d4243a' : '#f3d4d8',
    boxShadow: state.isFocused ? '0 0 0 4px rgba(212, 36, 58, 0.12)' : '0 1px 2px rgba(16, 24, 40, 0.04)',
    '&:hover': {
      borderColor: '#d4243a',
    },
    borderRadius: 14,
    padding: '0 4px',
    transition: 'border-color .2s, box-shadow .2s',
    cursor: 'pointer',
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? '#d4243a' : state.isFocused ? '#fff5f5' : 'white',
    color: state.isSelected ? 'white' : '#344054',
    cursor: 'pointer',
    padding: '0.65rem 0.9rem',
    borderRadius: 10,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    '&:active': {
      backgroundColor: '#d4243a',
      color: 'white',
    },
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 9999,
    borderRadius: 14,
    overflow: 'hidden',
    border: '1px solid #f3d4d8',
    boxShadow: '0 24px 48px -16px rgba(16, 24, 40, 0.25)',
  }),
  menuList: (provided) => ({
    ...provided,
    padding: 6,
  }),
  singleValue: (provided) => ({
    ...provided,
    color: '#101828',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
  }),
  placeholder: (provided) => ({
    ...provided,
    color: '#98a2b3',
  }),
  dropdownIndicator: (provided, state) => ({
    ...provided,
    color: state.isFocused ? '#d4243a' : '#98a2b3',
    transition: 'transform .25s, color .2s',
    transform: state.selectProps.menuIsOpen ? 'rotate(180deg)' : 'none',
    '&:hover': {
      color: '#d4243a',
    },
  }),
  clearIndicator: (provided) => ({
    ...provided,
    color: '#98a2b3',
    '&:hover': { color: '#d4243a' },
  }),
  indicatorSeparator: () => ({
    display: 'none',
  }),
};
const steps = [

  {
    target: '.ville',  // Ville selection
    content: t('menuoptions:tour.ville'),
    styles: {
      tooltip: {
        backgroundColor: '#f87171',
        color: '#fff',
        fontSize: '16px',
        padding: '12px',
        borderRadius: '8px',
      },
    },
  },
  {
    target: '.caisses-inactives',  // Caisses inactives
    content: t('menuoptions:tour.caisses-inactives'),
    styles: {
      tooltip: {
        backgroundColor: '#f87171',
        color: '#fff',
      },
    },
  },
  {
    target: '.changer-caisse',  // Changer de caisse
    content: t('menuoptions:tour.changer-caisse'),
    styles: {
      tooltip: {
        backgroundColor: '#f87171',
        color: '#fff',
      },
    },
  },
  {
    target: '.deconnexion',  // Target for the 'Déconnexion' button
    content: t('menuoptions:tour.deconnexion'),
    styles: {
      tooltip: {
        backgroundColor: '#f87171',  // Tooltip background color
        color: '#fff',  // Tooltip text color
        fontSize: '16px',  // Text size for tooltip
        borderRadius: '8px',  // Tooltip border radius
        padding: '12px',  // Padding for tooltip
      },
    },
  },
  {
    target: '.mon-compte',  // Mon compte menu item
    content: t('menuoptions:tour.mon-compte'),
    styles: {
      tooltip: {
        backgroundColor: '#f87171',
        color: '#fff',
        fontSize: '16px',
        borderRadius: '8px',
        padding: '12px',
      },
    },
  },
  {
    target: '.tableau-de-bord',  // Tableau de bord
    content: t('menuoptions:tour.tableau-de-bord'),
    styles: {
      tooltip: {
        backgroundColor: '#f87171',
        color: '#fff',
        fontSize: '16px',
        borderRadius: '8px',
        padding: '12px',
      },
    },
  },
  {
    target: '.catalogue',  // Catalogue
    content: t('menuoptions:tour.catalogue'),
    styles: {
      tooltip: {
        backgroundColor: '#f87171',
        color: '#fff',
        fontSize: '16px',
        borderRadius: '8px',
        padding: '12px',
      },
    },
  },
  {
    target: '.gestion-des-privilèges',  // Gestion des privilèges
    content: t('menuoptions:tour.gestion-des-privilèges'),
    styles: {
      tooltip: {
        backgroundColor: '#f87171',
        color: '#fff',
        fontSize: '16px',
        borderRadius: '8px',
        padding: '12px',
      },
    },
  },
  {
    target: '.client',  // Client menu item
    content: t('menuoptions:tour.client'),
    styles: {
      tooltip: {
        backgroundColor: '#f87171',
        color: '#fff',
        fontSize: '16px',
        borderRadius: '8px',
        padding: '12px',
      },
    },
  },
  {
    target: '.regles-de-remise',  // Règles de remise menu item
    content: t('menuoptions:tour.regles-de-remise'),
    styles: {
      tooltip: {
        backgroundColor: '#f87171',
        color: '#fff',
        fontSize: '16px',
        borderRadius: '8px',
        padding: '12px',
      },
    },
  },
  {
    target: '.gestion-de-stock',  // Gestion de stock menu item
    content: t('menuoptions:tour.gestion-de-stock'),
    styles: {
      tooltip: {
        backgroundColor: '#f87171',
        color: '#fff',
        fontSize: '16px',
        borderRadius: '8px',
        padding: '12px',
      },
    },
  },
  {
    target: '.facturation',  // Facturation / Devis menu item
    content: t('menuoptions:tour.facturation'),
    styles: {
      tooltip: {
        backgroundColor: '#f87171',
        color: '#fff',
        fontSize: '16px',
        borderRadius: '8px',
        padding: '12px',
      },
    },
  },
  {
    target: '.channel-manager',  // Channel Manager menu item
    content: t('menuoptions:tour.channel-manager'),
    styles: {
      tooltip: {
        backgroundColor: '#f87171',
        color: '#fff',
        fontSize: '16px',
        borderRadius: '8px',
        padding: '12px',
      },
    },
  },
  {
    target: '.integrations',  // Intégrations menu item
    content: t('menuoptions:tour.integrations'),
    styles: {
      tooltip: {
        backgroundColor: '#f87171',
        color: '#fff',
        fontSize: '16px',
        borderRadius: '8px',
        padding: '12px',
      },
    },
  },
  {
    target: '.materiel-de-caisse',  // Matériel de caisse menu item
    content: t('menuoptions:tour.materiel-de-caisse'),
    styles: {
      tooltip: {
        backgroundColor: '#f87171',
        color: '#fff',
        fontSize: '16px',
        borderRadius: '8px',
        padding: '12px',
      },
    },
  },
  {
    target: '.documentation',  // Documentation menu item
    content: t('menuoptions:tour.documentation'),
    styles: {
      tooltip: {
        backgroundColor: '#f87171',
        color: '#fff',
        fontSize: '16px',
        borderRadius: '8px',
        padding: '12px',
      },
    },
  },
  {
    target: '.actualites',  // Actualités & Nouveautés menu item
    content: t('menuoptions:tour.actualites'),
    styles: {
      tooltip: {
        backgroundColor: '#f87171',
        color: '#fff',
        fontSize: '16px',
        borderRadius: '8px',
        padding: '12px',
      },
    },
  },
  {
    target: '.nos-applications',  // Nos applications menu item
    content: t('menuoptions:tour.nos-applications'),
    styles: {
      tooltip: {
        backgroundColor: '#f87171',
        color: '#fff',
        fontSize: '16px',
        borderRadius: '8px',
        padding: '12px',
      },
    },
  },
];

/** GIFs encoded “play once” need a new URL periodically to restart the animation. */
const AI_ASSISTANT_GIF_REPLAY_MS = 2000;

const AiAssistantMenuGif = ({ src: baseSrc, className, style }) => {
  const [replayKey, setReplayKey] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setReplayKey((k) => k + 1), AI_ASSISTANT_GIF_REPLAY_MS);
    return () => clearInterval(id);
  }, []);
  const sep = String(baseSrc).includes('?') ? '&' : '?';
  const src = `${baseSrc}${sep}_replay=${replayKey}`;
  return <img key={replayKey} src={src} alt="" className={className} style={style} />;
};

// Custom Option Component: name, email and licence end of each point of sale
const CustomOption = (props) => {
  const { data, isSelected, isFocused, innerRef, innerProps } = props;
  return (
    <div
      ref={innerRef}
      {...innerProps}
      className={`mo-option ${isFocused ? 'is-focused' : ''} ${isSelected ? 'is-selected' : ''}`}
    >
      <span className="mo-option-avatar">{String(data.nom_societe || '?').trim().charAt(0).toUpperCase()}</span>
      <span className="mo-option-text">
        <strong>{data.nom_societe}</strong>
        <small>{data.email}</small>
      </span>
      <span className="mo-option-end">
        {isSelected ? <CheckIcon style={{ height: 18, width: 18 }} /> : <small>{t('menuoptions:until', { date: data.date_fin })}</small>}
      </span>
    </div>
  );
};

// Custom SingleValue Component
const CustomSingleValue = (props) => {
  const { data } = props;
  return (
    <components.SingleValue {...props}>
      <div className="flex flex-col">
        <span className="font-semibold text-gray-900">{data.nom_societe}</span>
        <span className="text-sm text-gray-600">{data.email}</span>
      </div>
    </components.SingleValue>
  );
};

const PointDeVente = (props) => {
  const [caisseList, setCaisseList] = useState([]);
  const [filteredCaisses, setFilteredCaisses] = useState([]);
  const [villes, setVilles] = useState([]);
  const [selectedVille, setSelectedVille] = useState(null);
  const [selectedPointDeVente, setSelectedPointDeVente] = useState(null);
  const [caissesStatus, setCaissesStatus] = useState('active');
  const [showError, setShowError] = useState(false);
  const [clickedMenuItem, setClickedMenuItem] = useState(null);
  const [runTour, setRunTour] = useState(false);
  const { history } = props;
  const [recherche, setRecherche] = useState('');
  const rechercheRef = useRef(null);
  const pdvSelectRef = useRef(null);




  const allMenuItems = [
    { name: 'Mon compte', icon: Person, route: '/mon-compte', class: 'mon-compte' },
    { name: 'Tableau de bord', icon: Tableau, route: '/tableau-de-bord', class: "tableau-de-bord" },
    { name: 'Catalogue', icon: Catalogue, route: '/catalogue', class: "catalogue" },
    { name: 'Gestion des privilèges', icon: Reglage, route: '/privileges', class: "gestion-des-privilèges" },
    { name: 'Client', icon: Client, route: '/listClient', class: "client" },
    { name: 'Règles de remise', icon: Remise, route: '/regles-de-reduction', class: "regles-de-remise" },
    { name: 'Gestion de stock', icon: Stock, route: '/stock', class: "gestion-de-stock" },
    { name: 'Facturation / Devis', icon: Facturation, route: '/dashboardfactures', class: "facturation" },
    { name: 'Channel Manager', icon: Cm_en, route: '/channel-manager/menu', class: "channel-manager" },
    { name: 'Intégrations', icon: Integration, route: '/integrations', class: "integrations" },
    { name: 'Market place', icon: Material, route: '/store-page', class: "materiel-de-caisse" }, 
     // { name: 'Avis client', icon: Reglage, route: '/notation' },
    // { name: 'Commandes en ligne', icon: Cm_en, route: '/Lalivraisonma' },
    { name: 'Documentation', icon: Docs, route: '/Lalivraison.ma', class: "documentation" },
    { name: 'Actualités & Nouveautés', icon: Cm_enLigne, route: '/Blog', class: "actualites" },
    { name: 'AI assistant', iconSrc: aiAssistantGif, route: '/assistant-ia', class: "ai-assistant" },
  ];
  // Session opened with a temporary password: the "Tableau de bord" section is not available.
  const menuItems = allMenuItems.filter(
    (item) => !(Number(ls.get('temporary_login')) === 1 && item.class === 'tableau-de-bord')
  );

  const { setSelectedOption_menu } = useContext(Context);

  // React Query: Fetch the list of Caisses (sales points) with caching
  const fetchCaisses = async () => {
    const url = `${BASE_URL}caisse/listCaisse`;
    const config = {
      headers: {
        'Authorization': 'Bearer ' + ls.get('token'),
      },
    };
    const params = {
      email: ls.get('email'),
    };
    const response = await axios.post(url, params, config);
    return response.data.data || [];
  };

  const { data: caisses, isLoading, isError, refetch } = useQuery({
    queryKey: ['caisses', ls.get('email')],
    queryFn: fetchCaisses,
    staleTime: 5 * 60 * 1000, // 5 minutes - data stays fresh for 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes - cache persists for 10 minutes
    refetchOnWindowFocus: false, // Don't refetch when window gains focus
    onSuccess: (data) => {
      console.log('📦 Data fetched/cached:', data.length, 'caisses');
    },
    onError: (error) => {
      console.error('Error fetching caisses:', error);
    }
  });

  // Update local state when React Query data changes
  useEffect(() => {
    document.title = `Backoffice`;

    if (caisses) {
      setCaisseList(caisses);

      // Initial filtering based on caissesStatus
      const initialFiltered = caisses.filter(
        (caisse) => caisse.alerte_paiement == (caissesStatus === 'active' ? 0 : 2)
      );
      console.log("initialFiltered", initialFiltered);
      
      setFilteredCaisses(initialFiltered);

      // Extract unique villes from the filtered caisses
      const villesData = [...new Set(initialFiltered.map((caisse) => caisse.ville))];
      console.log('villesData', villesData);
      setVilles(villesData);

    }
  }, [caisses, caissesStatus]);

  // Filter the caisses based on selected Ville and Status
  useEffect(() => {

    let filtered = [...caisseList];

    if (caissesStatus) {
      filtered = filtered.filter(
        (caisse) => caisse.alerte_paiement == (caissesStatus === 'active' ? 0 : 2)
      );

      const villesData = [...new Set(filtered.map((caisse) => caisse.ville))];
      setVilles(villesData);
    }

    if (selectedVille) {
      filtered = filtered.filter((caisse) => caisse.ville === selectedVille);
    }

    setFilteredCaisses(filtered);
    setSelectedPointDeVente(null); // Reset point de vente selection



    const encryptedCaisseId = ls.get('currentcaisse');
    const caisseName = ls.get('caissename');
    const caisseEmail = ls.get('emailCurrentCaisse');
    const encryptedDateFin = ls.get('datefin');



    if (encryptedCaisseId && caisseName && caisseEmail && encryptedDateFin) {
      try {
        const decryptedCaisseId = cryptr.decrypt(encryptedCaisseId);
        const decryptedDateFin = cryptr.decrypt(encryptedDateFin);
        const caisseId = parseInt(decryptedCaisseId, 10);

        const selectedCaisse = filtered.find((caisse) => caisse.id_caisse == caisseId);


        if (selectedCaisse) {
          setSelectedPointDeVente({
            label: `${selectedCaisse.email} - ${selectedCaisse.nom_societe} - ${selectedCaisse.date_fin}`,
            value: selectedCaisse.id_caisse,
            email: selectedCaisse.email,
            nom_societe: selectedCaisse.nom_societe,
            date_fin: selectedCaisse.date_fin,
          });
          setSelectedVille(selectedCaisse.ville);
        }
      } catch (error) {
        console.error('Error decrypting caisse data:', error);
      }
    }


    // Update villes based on the new filtered caisses

  }, [selectedVille, caisseList]);


  useEffect(() => {

    let filtered = [...caisseList];

    

    if (caissesStatus) {
      filtered = filtered.filter(
        (caisse) => caisse.alerte_paiement == (caissesStatus === 'active' ? 0 : 2)
      );
      const villesData = [...new Set(filtered.map((caisse) => caisse.ville))];
      setVilles(villesData);
    }

    if (selectedVille) {
      filtered = filtered.filter((caisse) => caisse.ville === selectedVille);
    }

    setFilteredCaisses(filtered);
    setSelectedPointDeVente(null); // Reset point de vente selection

    // Update villes based on the new filtered caisses



  }, [caissesStatus]);

  // Focuses the point of sale field and opens its list (react-select 3: ref.select).
  const ouvrirListePdv = () => {
    const ref = pdvSelectRef.current;
    const inner = ref && ref.select;
    if (inner && inner.focus) inner.focus();
    if (inner && inner.openMenu) inner.openMenu('first');
  };

  // Handle menu item click
  const handleMenuClick = (item) => {
    console.log("handleMenuClick" , item)

    if (item.name == "Documentation") {
      window.open("https://documentation.lacaisse.ma/", "_blank")
      return
    }



    if (!selectedPointDeVente) {
      setShowError(true);
      setClickedMenuItem(item.name);

      // Scroll to top and open the point of sale list: the user can choose at once.
      window.scrollTo({ top: 0, behavior: 'smooth' });
      setTimeout(ouvrirListePdv, 350);

      // Remove the jiggle animation after it completes (500ms)
      setTimeout(() => {
        setClickedMenuItem(null);
      }, 500);

      // Optionally, hide the error message after some time (e.g., 3 seconds)

    } else {
      setSelectedOption_menu(item.name);      
      localStorage.setItem('selectedOption', JSON.stringify(item.name));
      history.push(item.route);
    }
  };

  // Handle ville change
  const handleVilleChange = (selectedOption) => {
    ls.remove('currentcaisse');
    ls.remove('caissename');
    ls.remove('emailCurrentCaisse');
    ls.remove('datefin');

    setSelectedVille(selectedOption ? selectedOption.value : null);
  };

  // Handle point de vente change
  const handlePointDeVenteChange = (selectedOption) => {

    console.log("selectedOption", selectedOption)

    setShowError(false);
    setSelectedPointDeVente(selectedOption ? selectedOption : null);

    if (selectedOption) {
      handleClick(
        selectedOption.value,
        selectedOption.nom_societe,
        selectedOption.email,
        selectedOption.date_fin,
        selectedOption.token_api
      );
    }
  };

  // Handle caisses status change (active/inactive)
  const handleCaissesStatusChange = (selectedOption) => {
    ls.remove('currentcaisse');
    ls.remove('caissename');
    ls.remove('emailCurrentCaisse');
    ls.remove('datefin');
    setCaissesStatus(selectedOption ? selectedOption : 'active');
    setSelectedPointDeVente(null);
    setSelectedVille(null);
  };

  // handleClick function to manage local storage
  const handleClick = (caisseId, caisseName, caisseEmail, dateFin , token) => {

    caisse_master(caisseId);
    ls.set('tokenizer', md5(caisseId.toString()));
    ls.set('currentcaisse', cryptr.encrypt(caisseId.toString()));
    ls.set('caissename', caisseName);
    ls.set('emailCurrentCaisse', caisseEmail);
    ls.set('datefin', cryptr.encrypt(dateFin));
    ls.set('loaderbuttondiff', 1);
    ls.set('token_api', token);
  };

  // First visit: the tour starts once the pop-up of the page (database, PopupHost) is closed or
  // when there is none, so that the two never overlap.
  const tourPending = useRef(!localStorage.getItem('hasSeenTour'));
  const onPopupSettled = () => {
    if (!tourPending.current) return;
    tourPending.current = false;
    localStorage.setItem('hasSeenTour', 'true');
    setRunTour(true);
  };

  // 2FA pop-up (cta_action otp_activate): select the first point de vente if none is selected, then open
  // Mon compte directly on the activation (/mon-compte?2fa=1).
  const activateTwoFactor = () => {
    if (!ls.get('currentcaisse')) {
      const list = filteredCaisses.length ? filteredCaisses : caisseList;
      const first = list.find((caisse) => caisse.alerte_paiement == 0) || list[0];
      if (!first) {
        antdMessage.warning(t('menuoptions:noPosForAccount'));
        return;
      }
      handlePointDeVenteChange({
        label: `${first.email} - ${first.nom_societe} - ${first.date_fin}`,
        value: first.id_caisse,
        email: first.email,
        nom_societe: first.nom_societe,
        date_fin: first.date_fin,
        token_api: first.token_api,
      });
    }
    setSelectedOption_menu('Mon compte');
    localStorage.setItem('selectedOption', JSON.stringify('Mon compte'));
    history.push('/mon-compte?2fa=1');
  };


  const caisse_master = (id) => {
    let url = BASE_URL + "affichercaisses/" + id;

    if( ls.get('sc_uuid') ) {
      url += "?sc_uuid=" + ls.get('sc_uuid');
    }

    let config = {
      headers: {
        'Access-Control-Allow-Origin': '*',
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + ls.get('token'),
        'X-identifier-account': (typeof (ls.get('current_account_caisse')) !== "undefined" && ls.get('current_account_caisse') !== null) ? cryptr.decrypt(ls.get('current_account_caisse')) : (Disconnected()),
        'X-identifier-val': id
      }
    };
    axios.post(url, {}, config)
      .then(response => {

        let master = response.data.master
        let count = response.data.count
        ls.set('caissecount', count);
        ls.set('caissemaster', master);

      })
      .catch(error => {
        console.log(error);
        ls.set('caissecount', 0);
        ls.set('caissemaster', id)

      });
  };

  const startTour = () => {
    setRunTour(true);  // Trigger the tour to start
  };

  const restartTour = () => {
    const tourCompleted = localStorage.getItem('tourCompleted');
    if (tourCompleted === 'true') {
      setRunTour(false);  // Reset the tour
      localStorage.removeItem('tourCompleted');  // Clear completion status
    }
    setRunTour(true);  // Start the tour again
  };

  const signOut = (e) => {
    e.preventDefault();
    Connexion.signout(null, "/login");
  }

  // « / » puts the cursor in the module search (unless already typing somewhere).
  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target && e.target.tagName) || '';
      if (e.key === '/' && !/INPUT|TEXTAREA|SELECT/.test(tag) && !(e.target && e.target.isContentEditable)) {
        e.preventDefault();
        if (rechercheRef.current) rechercheRef.current.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Only one point of sale available (active ones): it is selected directly.
  useEffect(() => {
    if (isLoading || selectedPointDeVente || caissesStatus !== 'active' || filteredCaisses.length !== 1) return;
    const c = filteredCaisses[0];
    handlePointDeVenteChange({
      label: `${c.email} - ${c.nom_societe} - ${c.date_fin}`,
      value: c.id_caisse,
      email: c.email,
      nom_societe: c.nom_societe,
      date_fin: c.date_fin,
      token_api: c.token_api,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredCaisses, isLoading]);

  // Licence end of the chosen point of sale ("DD-MM-YYYY") -> days left.
  const joursRestants = (() => {
    const fin = selectedPointDeVente && selectedPointDeVente.date_fin;
    const m = String(fin || '').match(/^(\d{2})[-/](\d{2})[-/](\d{4})/);
    if (!m) return null;
    const end = new Date(`${m[3]}-${m[2]}-${m[1]}T23:59:59`);
    return Math.ceil((end - new Date()) / 86400000);
  })();
  const nbActives = caisseList.filter((c) => c.alerte_paiement == 0).length;
  const nbInactives = caisseList.filter((c) => c.alerte_paiement == 2).length;
  const heure = new Date().getHours();
  const salut = heure < 5 || heure >= 18 ? t('menuoptions:evening') : t('menuoptions:hello');
  const labelOf = (item) => t(`nav:${item.name}`);
  const descOf = (item) => t(`menuoptions:desc.${item.class}`);
  const q = recherche.trim().toLowerCase();
  const visibles = q
    ? menuItems.filter((it) => `${it.name} ${labelOf(it)} ${descOf(it)}`.toLowerCase().includes(q))
    : menuItems;

  return (

    <div className="mo-page">
      <PopupHost
        placement="menu"
        history={history}
        actions={{ otp_activate: activateTwoFactor }}
        onSettled={onPopupSettled}
      />

      {/* soft moving shapes behind the page */}
      <div className="mo-bg" aria-hidden="true">
        <span />
        <span />
        <span />
      </div>

      <header className="mo-header">
        <div className="mo-brand">
          <Logo className="mo-logo" />
          <p>
            {tm('menuoptions:tagline')}
          </p>
        </div>

        <div className="mo-header-actions">
          <button type="button" className="mo-ghost-btn" onClick={() => restartTour()}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <circle cx="12" cy="12" r="10" />
              <path d="M9.1 9a3 3 0 0 1 5.8 1c0 2-3 3-3 3" strokeLinecap="round" />
              <path d="M12 17h.01" strokeLinecap="round" />
            </svg>
            {t('menuoptions:tutorial')}
          </button>
          <LanguageSwitcher />
          <Menu as="div" className="mo-account deconnexion">
            <Menu.Button className="mo-account-btn">
              <span className="mo-avatar">{String(ls.get('email') || 'B').charAt(0).toUpperCase()}</span>
              <span className="mo-account-text">
                <strong>Backoffice</strong>
                <small>{ls.get('email') || ''}</small>
              </span>
              <svg className="mo-caret" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M6 9l6 6 6-6" />
              </svg>
            </Menu.Button>
            <Transition
              as={Fragment}
              enter="mo-dd-enter"
              enterFrom="mo-dd-from"
              enterTo="mo-dd-to"
              leave="mo-dd-leave"
              leaveFrom="mo-dd-to"
              leaveTo="mo-dd-from"
            >
              <Menu.Items className="mo-dropdown">
                <Menu.Item>
                  {({ active }) => (
                    <button onClick={signOut} className={`mo-dropdown-item ${active ? 'is-active' : ''}`}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      {t('menuoptions:signOut')}
                    </button>
                  )}
                </Menu.Item>
              </Menu.Items>
            </Transition>
          </Menu>
        </div>
      </header>

      <Joyride
  steps={steps}
  continuous={true}
  showSkipButton={true}
  showProgress={true}
  run={runTour}  // Start the tour when runTour is true
  hideCloseButton={true}
  disableOverlay={false}
  spotlightClicks={true}
  callback={(data) => {
    if (data.status === 'finished' || data.status === 'skipped') {
      setRunTour(false);  // Stop the tour after it is finished or skipped
      localStorage.setItem('tourCompleted', 'true');  // Mark tour as completed
    }
  }} // Stop tour when finished or skipped
  styles={{
    options: {
      zIndex: 1000,
      backgroundColor: '#f9fafb',
      arrowColor: '#f87171',
      primaryColor: '#d4243a',
      textColor: '#2d3748',
    },
    tooltip: {
      fontSize: '15px',
      borderRadius: '14px',
      boxShadow: '0 20px 40px -12px rgba(0, 0, 0, 0.35)',
      padding: '15px',
    },
    overlay: {
      backgroundColor: 'rgba(16, 24, 40, 0.5)',
    },
  }}
  locale={{
    back: t('menuoptions:tour.back'),
    next: t('menuoptions:tour.next'),
    skip: t('menuoptions:tour.skip'),
    last: t('menuoptions:tour.last'),
  }}
/>

      <main className="mo-main">
        {/* ------------------------------------------------ point of sale */}
        <section className={`mo-pdv ${showError ? 'is-error' : ''}`}>
          <div className="mo-pdv-glow" aria-hidden="true" />
          <div className="mo-pdv-top">
            <div>
              <span className="mo-eyebrow">{t('menuoptions:eyebrow')}</span>
              <h1>
                {salut} <span className="mo-wave">👋</span>
              </h1>
              <p>
                {selectedPointDeVente
                  ? t('menuoptions:ready')
                  : t('menuoptions:choose')}
              </p>
            </div>

            {/* Active / inactive points of sale (class kept for the tutorial) */}
            <div className="mo-status caisses-inactives" role="tablist" aria-label={t('menuoptions:statusLabel')}>
              <span className={`mo-status-pill ${caissesStatus === 'active' ? '' : 'is-right'}`} aria-hidden="true" />
              <button
                type="button"
                role="tab"
                aria-selected={caissesStatus === 'active'}
                className={caissesStatus === 'active' ? 'is-on' : ''}
                onClick={() => caissesStatus !== 'active' && handleCaissesStatusChange('active')}
              >
                {t('menuoptions:active')} <em>{nbActives}</em>
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={caissesStatus !== 'active'}
                className={caissesStatus !== 'active' ? 'is-on' : ''}
                onClick={() => caissesStatus === 'active' && handleCaissesStatusChange('inactive')}
              >
                {t('menuoptions:inactive')} <em>{nbInactives}</em>
              </button>
            </div>
          </div>

          <div className="mo-fields">
            <div className="mo-field ville">
              <div className="mo-field-head">
                <label htmlFor="ville-select">
                  <span className="mo-step">1</span> {t('menuoptions:city')}
                </label>
                <button
                  type="button"
                  className={`mo-refresh ${isLoading ? 'is-spinning' : ''}`}
                  onClick={() => refetch()}
                  disabled={isLoading}
                  title={t('menuoptions:refresh')}
                  aria-label={t('menuoptions:refreshShort')}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                    <path d="M21 12a9 9 0 1 1-3-6.7L21 8" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M21 3v5h-5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
              </div>
              <Select
                id="ville-select"
                options={villes.map((ville) => ({ label: ville, value: ville }))}
                onChange={(option) => {
                  handleVilleChange(option);
                  // City chosen: go straight to the point of sale list.
                  if (option) setTimeout(ouvrirListePdv, 0);
                }}
                value={selectedVille ? { label: selectedVille, value: selectedVille } : null}
                styles={customSelectStyles}
                placeholder={isLoading ? t('menuoptions:loading') : t('menuoptions:allCities')}
                isClearable
                noOptionsMessage={() => t('menuoptions:noCity')}
                className="react-select-container"
                classNamePrefix="react-select"
              />
            </div>

            <div className="mo-field changer-caisse">
              <div className="mo-field-head">
                <label htmlFor="pdv-select">
                  <span className={`mo-step ${selectedPointDeVente ? 'is-done' : ''}`}>
                    {selectedPointDeVente ? '✓' : '2'}
                  </span>{' '}
                  {t('menuoptions:pos')}
                </label>
                {filteredCaisses.length > 0 && (
                  <span className="mo-count">
                    {t('menuoptions:available', { count: filteredCaisses.length })}
                  </span>
                )}
              </div>
              <Select
                id="pdv-select"
                ref={pdvSelectRef}
                options={filteredCaisses.map((caisse) => ({
                  label: `${caisse.email} - ${caisse.nom_societe} - ${caisse.date_fin}`,
                  value: caisse.id_caisse,
                  email: caisse.email,
                  nom_societe: caisse.nom_societe,
                  date_fin: caisse.date_fin,
                  token_api: caisse.token_api
                }))}
                onChange={handlePointDeVenteChange}
                value={selectedPointDeVente}
                styles={customSelectStyles}
                placeholder={isLoading ? t('menuoptions:loading') : t('menuoptions:selectPos')}
                noOptionsMessage={() => t('menuoptions:noPos')}
                className="react-select-container"
                classNamePrefix="react-select"
                components={{
                  Option: CustomOption,
                  SingleValue: CustomSingleValue,
                }}
              />
            </div>
          </div>

          {selectedPointDeVente && joursRestants !== null && (
            <div className={`mo-licence ${joursRestants <= 30 ? 'is-warn' : ''} ${joursRestants < 0 ? 'is-over' : ''}`}>
              <span className="mo-licence-dot" />
              {joursRestants < 0
                ? t('menuoptions:licence.expired', { date: selectedPointDeVente.date_fin })
                : joursRestants <= 30
                ? t('menuoptions:licence.soon', { count: joursRestants, date: selectedPointDeVente.date_fin })
                : t('menuoptions:licence.active', { date: selectedPointDeVente.date_fin })}
            </div>
          )}

          {showError && (
            <div className="mo-error" role="alert">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <circle cx="12" cy="12" r="10" />
                <path d="M12 8v4M12 16h.01" strokeLinecap="round" />
              </svg>
              {t('menuoptions:selectFirst')}
            </div>
          )}
        </section>

        {/* ------------------------------------------------ modules */}
        <div className="mo-modules-head">
          <h2>{t('menuoptions:modules')}</h2>
          <div className="mo-search">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <circle cx="11" cy="11" r="7" />
              <path d="M20 20l-3.5-3.5" strokeLinecap="round" />
            </svg>
            <input
              ref={rechercheRef}
              value={recherche}
              onChange={(e) => setRecherche(e.target.value)}
              onKeyDown={(e) => {
                // Entrée opens the first module found, Échap clears the search.
                if (e.key === 'Enter' && visibles.length > 0) handleMenuClick(visibles[0]);
                if (e.key === 'Escape') setRecherche('');
              }}
              placeholder={t('menuoptions:searchModule')}
              aria-label={t('menuoptions:searchModuleAria')}
            />
          </div>
        </div>

        <div className={`mo-grid ${selectedPointDeVente ? '' : 'is-locked'}`}>
          {visibles.map((item, i) => (
            <button
              type="button"
              key={item.name}
              onClick={() => handleMenuClick(item)}
              style={{ animationDelay: `${Math.min(i, 14) * 45}ms` }}
              className={`mo-tile ${item.class} ${item.iconSrc ? 'is-ai' : ''} ${clickedMenuItem === item.name ? 'is-jiggle' : ''}`}
            >
              <span className="mo-tile-icon">
                {item.iconSrc ? (
                  <AiAssistantMenuGif src={item.iconSrc} className="mo-tile-gif" />
                ) : (
                  <item.icon />
                )}
              </span>
              <span className="mo-tile-text">
                <strong>{labelOf(item)}</strong>
                <small>{descOf(item)}</small>
              </span>
              <svg className="mo-tile-go" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <path d="M5 12h14M13 6l6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          ))}
          {visibles.length === 0 && <div className="mo-empty">{t('menuoptions:noModule', { search: recherche })}</div>}
        </div>
      </main>

      <footer className="mo-footer" aria-hidden="true">
        <FooterSvg alt="" className="w-full h-auto" />
      </footer>


    </div>

  );
};

// Create QueryClient instance for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,      // 5 minutes
      cacheTime: 100 * 60 * 1000,      // 10 minutes
      refetchOnWindowFocus: false,    // Don't refetch on window focus
      retry: 1,                        // Retry failed requests once
    },
  },
});

// Wrapper component with QueryClientProvider
const PointDeVenteWithProvider = (props) => {
  return (
    <QueryClientProvider client={queryClient}>
      <PointDeVente {...props} />
    </QueryClientProvider>
  );
};

export default withRouter(PointDeVenteWithProvider);
